import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { RangeDateTypeHr } from 'src/types/date.type';
import { FlatItem, TicketResponses } from 'src/types/elements.type';
import { ApprovalStatus } from 'src/types/helpdesk/ticket.type';
import {
  BusinessTripTicketAdd,
  BusinessTripTicketConfirm,
  IBusinessTripTicket,
  IBusinessTripTicketDetail
} from 'src/types/hr_ticket/businessTripTicket.type';
import { PagedList } from 'src/types/pagedList.type';
import { ITicketParams } from 'src/types/params.type';
import { http, httpV2 } from '@martech-solution/utils/utils';

const controller = 'hr/business-trip-tickets';

export const getBusinessTripTickets = async (params: ITicketParams) => {
  return await http.get<PagedList<IBusinessTripTicket>>(controller, {
    params: params
  });
};

export const getBusinessTripTicketsApproval = async (params: ITicketParams) => {
  return await http.get<PagedList<IBusinessTripTicket>>(`${controller}/waiting-approval`, {
    params: params
  });
};

export const getBusinessTripTicketDetailsUsed = async (fromDate: string, toDate: string, employeeCode?: string) => {
  return await http.get<IBusinessTripTicketDetail[]>(`${controller}/used-business-trip-details/${fromDate}/${toDate}`, {
    params: {
      employeeCode
    }
  });
};

export const getBusinessTripTicketsHrConfirm = async (
  fromDate: Date,
  toDate: Date,
  searchCondition: string | undefined,
  departmentId?: number | undefined,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  confirmType?: ConfirmTypeEn | undefined
) => {
  const newFromDate = fromDate.toISOString();
  const newToDate = toDate.toISOString();
  return await httpV2.get<PagedList<IBusinessTripTicket>>(`${controller}/hr-confirm/${newFromDate}/${newToDate}`, {
    params: {
      searchCondition,
      departmentId,
      page,
      pageSize,
      orderBy,
      confirmType
    }
  });
};

export const getBusinessTripTicketsHrConfirmV2 = async (
  fromDate: Date,
  toDate: Date,
  searchCondition: string | undefined,
  departmentId?: number | undefined,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  confirmType?: ConfirmTypeEn | undefined
) => {
  const newFromDate = fromDate.toISOString();
  const newToDate = toDate.toISOString();
  return await httpV2.get<PagedList<IBusinessTripTicket>>(`${controller}/hr-confirm/${newFromDate}/${newToDate}`, {
    params: {
      searchCondition,
      departmentId,
      page,
      pageSize,
      orderBy,
      confirmType
    }
  });
};

export const getBusinessTripTicket = async (id: number) => {
  return await http.get<IBusinessTripTicket>(`${controller}/${id}`);
};

export const getBusinessTripTicketsDropdown = async () => {
  return await http.get<FlatItem[]>(`${controller}/dropdown`);
};

export const addBusinessTripTicket = async (businessTripTicket: BusinessTripTicketAdd) => {
  return await http.post(controller, businessTripTicket);
};
export const addMultipleBusinessTripTicket = async (businessTripTickets: BusinessTripTicketAdd[]) => {
  return await http.post(`${controller}/multiple`, businessTripTickets);
};

export const updateBusinessTripTicket = async (id: number, businessTripTicket: BusinessTripTicketAdd) => {
  return await http.put(`${controller}/${id}`, businessTripTicket);
};

export const deleteBusinessTripTicket = async (id: number) => {
  return await http.delete(`${controller}/${id}`);
};

export const confirmBusinessTripTicket = async (id: number, businessTripTicketDetail: BusinessTripTicketConfirm) => {
  return await http.put(`${controller}/${id}/confirm-ticket`, {
    confirmNote: businessTripTicketDetail.confirmNote,
    isConfirm: businessTripTicketDetail.confirmType,
    confirms: businessTripTicketDetail.confirms
  });
};

export const cancelBusinessTripTicket = async (id: number) => {
  return await http.put(`${controller}/${id}/cancel`);
};

export const approvalBusinessTripTickets = async (
  ticketResponses: TicketResponses[],
  approvalStatus: ApprovalStatus,
  note?: string
) => {
  return await http.put(`${controller}/approvals`, {
    ticketResponses: ticketResponses,
    approvalStatus,
    note: note ? note : ''
  });
};

export const cancelApprovalBusinessTripTickets = async (id: string, step: number) => {
  return await http.put(`${controller}/${id}/cancel-approval`, { step });
};

export const printBusinessTripTicket = async (id: number) => {
  return await http.get(`${controller}/print`, {
    params: {
      id: id
    },
    responseType: 'blob'
  });
};

export const getRefBusinessTripTicketsForTicket = async (refTicketId: string) => {
  return await http.get<IBusinessTripTicket[]>(`${controller}/get-ref-tickets/${refTicketId}`);
};

export const getBusinessTripWaitingProcessingCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-ticket-waiting-processing`, {
    params: params
  });
};

export const getMyBusinessTripTicketCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-my-tickets`, {
    params: params
  });
};

export const getBusinessTripTicketApprovalCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-handle-tickets`, {
    params: params
  });
};

export const getBusinessTripTicketHrConfirmCount = async (params: RangeDateTypeHr) => {
  return await httpV2.get(`${controller}/count-hr-confirm-tickets`, {
    params: params
  });
};

export const hrConfirmBusinessTripTicket = async (
  id: number | undefined,
  businessTripTicketDetail: BusinessTripTicketConfirm
) => {
  return await httpV2.put(`${controller}/hr-confirm`, {
    id,
    businessTripTicketDetails: businessTripTicketDetail.confirms,
    confirmType: businessTripTicketDetail.confirmType,
    confirmNote: businessTripTicketDetail.confirmNote
  });
};

export const getBusinessTripTicketsReport = async (
  fromDate?: Date,
  toDate?: Date,
  departmentId?: number | undefined,
  searchCondition?: string | undefined,
  confirmType?: ConfirmTypeEn | undefined
) => {
  return await httpV2.get<IBusinessTripTicket[]>(`${controller}/hr-report`, {
    params: {
      fromDate,
      toDate,
      departmentId,
      searchCondition,
      confirmType
    }
  });
};

export const approvalQuickBusinessTripTickets = async (
  ticketIds: number[],
  approvalStatus: ApprovalStatus,
  note?: string
) => {
  return await httpV2.put(`${controller}/approvals`, ticketIds, {
    params: { approvalStatus, note }
  });
};
